/**
 * Ellenség-archetípusok — tiszta adat, logika nélkül.
 *
 * A számok X GDD-jéből (`parbeszed/05-X-gdd.md`, 2. pont) származnak,
 * másodperc→tick váltással. Az időzítések tickben élnek, hogy a szimuláció
 * frekvenciafüggetlen maradjon.
 *
 * Gazda: X — a balanszot te hangolod, C csak beírta az induló táblát.
 */

import { secs, type DamageKind } from '../core/contracts'
import { DEG } from '../core/math'

export interface EnemyAttack {
  kind: 'melee' | 'hitscan' | 'projectile'
  /** Sebzés-tartomány; a dobás a magolt PRNG haranggörbéjével történik. */
  damage: [number, number]
  damageKind: DamageKind
  /** Hány lövés egy sorozatban (sentinel: 3). */
  shots: number
  /** Sorozaton belüli késleltetés tickben. */
  shotInterval: number
  range: number
  /** Ennél közelebb nem indít lövedéktámadást. */
  minRange?: number
  /** Szórás fokban (hitscan). */
  spreadDeg: number
  /** Annak esélye, hogy a hitscan lövés a célra irányul. */
  accuracy?: number
  /** Egy szándékosan elvétett hitscan lövés eltérése fokban. */
  missSpreadDeg?: number
  /** Két támadás közti minimális szünet tickben. */
  cooldown: number
  /** A telegráf hossza tickben — a támadás ENNYIVEL később üt be. */
  telegraph: number
  /** Első észlelés után ennyi idő biztosan eltelik az első telegráf előtt. */
  firstSightDelay: number
  /** Hound-roham sebessége a telegráf alatt (egység/mp). */
  lungeSpeed?: number
  /** Lövedék-típus, ha `kind === 'projectile'`. */
  projectile?: string
}

export interface EnemyArchetype {
  id: string
  name: string
  health: number
  radius: number
  height: number
  eyeHeight: number
  /** Egység/mp. */
  speed: number
  sightRange: number
  /** Látómező fél-szöge radiánban (a teljes FOV fele). */
  sightHalfFov: number
  attack: EnemyAttack
  /** Nem halálos találat után ekkora eséllyel megtorpan. */
  painChance: number
  painDuration: number
  /** A `strafe` állapot hossza tickben. */
  strafeDuration: number
  /** Támadás után ekkora eséllyel oldalaz, egyébként közelít. */
  strafeChance: number
  deathDuration: number
  sounds: { sight: string; attack: string; pain: string; death: string }
  /** Halálkor ledobott tárgy(ak) és esélyük. */
  drops: { type: string; chance: number }[]
  /** A gib-részecskék színe. */
  gibColor: number
}

export const ENEMIES: Record<string, EnemyArchetype> = {
  grunt: {
    id: 'grunt',
    name: 'Őrszem',
    health: 45,
    radius: 0.45,
    height: 1.75,
    eyeHeight: 1.5,
    speed: 4.0,
    sightRange: 28,
    sightHalfFov: 55 * DEG,
    attack: {
      kind: 'hitscan',
      damage: [8, 8],
      damageKind: 'bullet',
      shots: 1,
      shotInterval: 0,
      range: 28,
      spreadDeg: 4.5,
      accuracy: 0.55,
      missSpreadDeg: 18,
      cooldown: secs(1.1),
      telegraph: secs(0.35),
      firstSightDelay: secs(0.75),
    },
    painChance: 0.35,
    painDuration: secs(0.3),
    strafeDuration: secs(0.9),
    strafeChance: 0.6,
    deathDuration: secs(0.7),
    sounds: { sight: 'sightGrunt', attack: 'pistol', pain: 'enemyPain', death: 'enemyDeath' },
    drops: [{ type: 'bullets', chance: 0.55 }],
    gibColor: 0xa53347,
  },

  imp: {
    id: 'imp',
    name: 'Tűzvető',
    health: 80,
    radius: 0.55,
    height: 1.9,
    eyeHeight: 1.55,
    speed: 3.2,
    sightRange: 32,
    sightHalfFov: 60 * DEG,
    attack: {
      kind: 'projectile',
      damage: [20, 20],
      damageKind: 'fire',
      shots: 1,
      shotInterval: 0,
      range: 30,
      // Négy egység alatt a 14 egység/mp-es tűzgolyó gyakorlatilag
      // azonnali találatnak érződik. Az imp távoli nyomást gyakorol;
      // közeli fenyegetésre ott a hound.
      minRange: 4,
      spreadDeg: 1.5,
      cooldown: secs(1.5),
      telegraph: secs(0.5),
      firstSightDelay: secs(0.85),
      projectile: 'impFire',
    },
    painChance: 0.25,
    painDuration: secs(0.3),
    strafeDuration: secs(0.9),
    strafeChance: 0.6,
    deathDuration: secs(0.7),
    sounds: { sight: 'sightImp', attack: 'plasmaFire', pain: 'enemyPain', death: 'enemyDeath' },
    drops: [{ type: 'shells', chance: 0.4 }],
    gibColor: 0x862033,
  },

  hound: {
    id: 'hound',
    name: 'Rohamvadász',
    health: 55,
    radius: 0.5,
    height: 1.1,
    eyeHeight: 0.9,
    speed: 7.5,
    sightRange: 24,
    sightHalfFov: 70 * DEG,
    attack: {
      kind: 'melee',
      damage: [18, 18],
      damageKind: 'melee',
      shots: 1,
      shotInterval: 0,
      range: 1.7,
      spreadDeg: 0,
      cooldown: secs(0.85),
      telegraph: secs(0.3),
      firstSightDelay: secs(0.65),
      // 0,3 mp alatt ~3,9 egységet tesz meg: a 2,5–5 egységes ugrás célba ér.
      lungeSpeed: 13,
    },
    painChance: 0.45,
    painDuration: secs(0.25),
    strafeDuration: secs(0.5),
    // A vadász nem oldalaz: a szerepe a folyamatos nyomás.
    strafeChance: 0.1,
    deathDuration: secs(0.6),
    sounds: { sight: 'sightHound', attack: 'melee', pain: 'enemyPain', death: 'enemyDeath' },
    drops: [{ type: 'medkitSmall', chance: 0.4 }],
    gibColor: 0x671626,
  },

  sentinel: {
    id: 'sentinel',
    name: 'Őrálló',
    health: 160,
    radius: 0.65,
    height: 2.3,
    eyeHeight: 2.0,
    speed: 2.6,
    sightRange: 38,
    sightHalfFov: 50 * DEG,
    attack: {
      kind: 'hitscan',
      damage: [9, 9],
      damageKind: 'bullet',
      shots: 3,
      shotInterval: secs(0.12),
      range: 36,
      spreadDeg: 3,
      accuracy: 0.72,
      missSpreadDeg: 14,
      cooldown: secs(1.8),
      telegraph: secs(0.45),
      firstSightDelay: secs(0.9),
    },
    painChance: 0.15,
    painDuration: secs(0.25),
    strafeDuration: secs(0.9),
    strafeChance: 0.45,
    deathDuration: secs(0.8),
    sounds: { sight: 'sightSentinel', attack: 'chaingun', pain: 'enemyPain', death: 'enemyDeath' },
    drops: [
      { type: 'bullets', chance: 0.8 },
      // 0.35-ről emelve: a cella az egyetlen szűk lőszer a 3. pályán, és a
      // plazma az egyetlen hatékony brute-ellenes fegyver. A golyó-dropot
      // szándékosan NEM emeltük, abból nincs hiány.
      { type: 'cells', chance: 0.6 },
    ],
    gibColor: 0x429aa8,
  },

  brute: {
    id: 'brute',
    name: 'Ostromgép',
    // 600-ról csökkentve. Indoklás: `parbeszed/27-C-brute-dontes.md`.
    // 3 × 600 a pálya összéletének 39%-a volt, és egyetlen brute elvitte a
    // teljes plazmakészlet 79%-át — a pálya csak szerencsés dropokkal volt
    // teljesíthető. 450 mellett is messze ez a legkeményebb ellenfél
    // (a sentinel csaknem háromszorosa).
    health: 450,
    radius: 1.0,
    height: 3.2,
    eyeHeight: 2.7,
    speed: 2.2,
    sightRange: 40,
    sightHalfFov: 80 * DEG,
    attack: {
      kind: 'projectile',
      damage: [28, 28],
      damageKind: 'explosion',
      shots: 1,
      shotInterval: 0,
      range: 34,
      spreadDeg: 1,
      cooldown: secs(2.5),
      telegraph: secs(0.8),
      firstSightDelay: secs(0.9),
      projectile: 'bruteBolt',
    },
    painChance: 0.1,
    painDuration: secs(0.2),
    strafeDuration: 0,
    // Az ostromgép sosem oldalaz — mindig jön. Ez a fenyegetése lényege.
    strafeChance: 0,
    deathDuration: secs(1.2),
    sounds: { sight: 'sightBrute', attack: 'rocketFire', pain: 'enemyPain', death: 'enemyDeath' },
    drops: [
      { type: 'rockets', chance: 1 },
      { type: 'medkitLarge', chance: 1 },
    ],
    gibColor: 0x8b5a2d,
  },
}

export interface ProjectileArchetype {
  id: string
  speed: number
  radius: number
  /** Robban-e becsapódáskor. */
  splashRadius: number
  splashDamage: number
  color: number
  /** Élettartam tickben, mielőtt magától elenyészik. */
  lifetime: number
}

export const PROJECTILES: Record<string, ProjectileArchetype> = {
  // A GDD szerint a lövedék kikerülhető: lassabb, mint a játékos oldalazása.
  impFire: { id: 'impFire', speed: 14, radius: 0.28, splashRadius: 0, splashDamage: 0, color: 0xff8a2b, lifetime: secs(5) },
  bruteBolt: { id: 'bruteBolt', speed: 16, radius: 0.4, splashRadius: 3.2, splashDamage: 18, color: 0xff3b1f, lifetime: secs(5) },
  plasmaBolt: { id: 'plasmaBolt', speed: 42, radius: 0.2, splashRadius: 0, splashDamage: 0, color: 0x8affff, lifetime: secs(3) },
  // A játékos-rakéta GDD-je 16 egységes sugarú, legfeljebb 70-es robbanást ír
  // elő. A sebzést a splash lineárisan csökkenti; saját találat 0,64-es
  // faktorral így legfeljebb 44,8 (azaz legfeljebb 45) lehet.
  rocket: { id: 'rocket', speed: 26, radius: 0.3, splashRadius: 16, splashDamage: 70, color: 0xffd98a, lifetime: secs(6) },
}
